
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Currency {
  id: string;
  name_ar: string;
  name_en: string;
  iso_code: string;
  price_margin: number;
  rate_to_sar: number;
  rate_from_sar: number;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

export const useCurrencies = () => {
  return useQuery({
    queryKey: ["currencies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currencies")
        .select("*")
        .order("name_ar");

      if (error) throw error;
      return data as Currency[];
    },
  });
};

export const useCreateCurrency = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (currency: Omit<Currency, 'id' | 'created_at' | 'updated_at' | 'last_updated'>) => {
      const { data, error } = await supabase
        .from("currencies")
        .insert([currency])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      toast({
        title: "تم الحفظ",
        description: "تم إضافة العملة بنجاح",
      });
    },
    onError: (error) => {
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة العملة",
        variant: "destructive",
      });
      console.error("Error creating currency:", error);
    },
  });
};

export const useUpdateCurrency = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...currency }: Partial<Currency> & { id: string }) => {
      const { data, error } = await supabase
        .from("currencies")
        .update({ ...currency, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      toast({
        title: "تم التحديث",
        description: "تم تحديث العملة بنجاح",
      });
    },
    onError: (error) => {
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث العملة",
        variant: "destructive",
      });
      console.error("Error updating currency:", error);
    },
  });
};

export const useDeleteCurrency = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("currencies")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      toast({
        title: "تم الحذف",
        description: "تم حذف العملة بنجاح",
      });
    },
    onError: (error) => {
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف العملة",
        variant: "destructive",
      });
      console.error("Error deleting currency:", error);
    },
  });
};
